import type { WidgetDataItem } from "@/hooks/useWidgetData";
import type {
  DynamicListWidgetConfig,
  StaticListDataSource,
  StaticListItem,
  StaticListItemType,
} from "@/models/dashboard/WidgetConfig";
import type { FavoriteLegislation } from "@/models/users/User";
import {
  parseLegislationOriginalId,
  resolveStaticListItemType,
  StaticListResolvedInfo,
} from "./staticListItems";

/** Everything the favorite toggle needs to add or remove one source. */
export interface FavoriteSourceTarget {
  externalId: string;
  datasource: string;
  title: string;
  internalId?: string;
}

/**
 * Favorited sources, keyed by external id: that is the only key the remove
 * endpoint takes, and it is what the Kilder table matches rows on.
 */
export const buildFavoriteSourceIndex = (
  favoriteLegislations: FavoriteLegislation[] | undefined
): Set<string> =>
  new Set(
    (favoriteLegislations ?? [])
      .map((favorite) => favorite.externalId)
      .filter((externalId): externalId is string => Boolean(externalId))
  );

export const isSourceFavorited = (
  favoriteSourceIds: Set<string>,
  externalId: string
): boolean => favoriteSourceIds.has(externalId);

/**
 * The favorite target for a dynamic list row, or null when the row is not a
 * source or is missing the identity a favorite is stored under.
 */
export const resolveDynamicListSourceTarget = (
  item: WidgetDataItem,
  dataSource: DynamicListWidgetConfig["dataSource"]
): FavoriteSourceTarget | null => {
  if (dataSource !== "sources") return null;
  if (!item.externalId || !item.datasource) return null;

  return {
    externalId: item.externalId,
    datasource: item.datasource,
    title: item.title.trim(),
    internalId: item.id || undefined,
  };
};

/**
 * The favorite target for a static list row. Legislation entries store their
 * identity as "externalId|datasource", and the favorite is saved under the
 * name the row shows — the custom name when the widget has one, trimmed, since
 * a widget name is free text and the favorite is stored verbatim. The title is
 * empty while the lookup is in flight or has failed; see canToggleFavorite.
 */
export const resolveStaticListSourceTarget = (
  item: StaticListItem,
  itemType: StaticListItemType,
  resolved: StaticListResolvedInfo
): FavoriteSourceTarget | null => {
  if (itemType !== "source") return null;

  const parsed = parseLegislationOriginalId(item.originalId);
  if (!parsed) return null;

  return {
    externalId: parsed.externalId,
    datasource: parsed.datasource,
    title: item.customName?.trim() || resolved.title?.trim() || "",
    internalId: resolved.internalId,
  };
};

/**
 * Whether the row can offer a favorite toggle. Adding stores the title, so a
 * row whose title has not resolved cannot be favorited — but removing takes
 * the external id alone, so an already favorited row keeps its star either
 * way and never becomes impossible to unfavorite from here.
 */
export const canToggleFavorite = (
  target: FavoriteSourceTarget | null,
  isFavorite: boolean
): target is FavoriteSourceTarget =>
  target !== null && (isFavorite || Boolean(target.title));

/**
 * Whether a static list needs the favorite column: a sources list always
 * does, and a mixed one only once it actually holds a source.
 */
export const staticListShowsFavoriteColumn = (
  items: StaticListItem[],
  dataSource: StaticListDataSource
): boolean => {
  if (dataSource === "sources") return true;
  if (dataSource === "workspace-documents") return false;

  return items.some(
    (item) => resolveStaticListItemType(item, dataSource) === "source"
  );
};
